import { Modal, Paper, Stack } from '@mui/material';
import { CircleCloseIcon } from 'Components/CircleCloseIcon';
import { NextPrevButton } from 'Components/NextPrevButton';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { TextNotification } from './TextNotification';
import { useMsal } from '@azure/msal-react';
import { ExitDoor } from 'ImageComponents/ExitDoor';
import { useCallback, useEffect } from 'react';
import { activityGuardTimerAlertTimeoutMs } from 'Config';

interface ILogoutWarningModal {
  isWarning: boolean;
  closeModal: Function;
}
export const LogoutWarningAndNotificationModal = ({
  isWarning,
  closeModal,
}: ILogoutWarningModal) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.General,
  ).activityGuard;
  const isDesktop = useIsDesktop();
  const { instance } = useMsal();
  const loginHandler = useCallback(async () => {
    closeModal();
    await instance.loginRedirect();
  }, [closeModal, instance]);
  const logoutHandler = useCallback(async () => {
    sessionStorage.clear();
    closeModal();
    await instance.logoutRedirect();
  }, [closeModal, instance]);
  useEffect(() => {
    if (!isWarning) {
      const id = window.setTimeout(() => {
        void logoutHandler();
      }, 0);
      return () => clearTimeout(id);
    }
    const timeoutId = window.setTimeout(() => {
      void logoutHandler();
    }, activityGuardTimerAlertTimeoutMs);
    return () => clearTimeout(timeoutId);
  }, [isWarning, logoutHandler]);
  return (
    <Modal
      open
      sx={{
        zIndex: 2000,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
          },
        },
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: isDesktop ? '770px' : '355px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          padding: '16px 16px 48px 16px',
          bgcolor: '#E7EDFF',
          borderRadius: '16px',
          border: 'none',
          boxShadow: '-2px -2px 10px 0px #081E540A',
          ':focus': {
            outline: 'none',
          },
        }}
      >
        <CircleCloseIcon
          alt={dictionary.closeModal}
          sx={{ ml: 'auto', cursor: 'pointer' }}
          onClick={() => (isWarning ? closeModal() : loginHandler())}
          onKeyDown={onEnterOrEscapeKeyDown}
        />
        <Stack
          sx={{
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <TextNotification isWarning={isWarning} />
          <Stack
            sx={{
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <NextPrevButton
              text={
                isWarning ? dictionary.continueActivity : dictionary.connect
              }
              callback={() => (isWarning ? closeModal() : loginHandler())}
              isBlue
              isNext
            />
            <NextPrevButton
              text={isWarning ? dictionary.logout : dictionary.toHomePage}
              hideArrowIcon
              icon={isWarning ? <ExitDoor width={22} height={22} /> : undefined}
              callback={() =>
                isWarning
                  ? logoutHandler()
                  : (window.location.href =
                      import.meta.env.VITE_APP_MARKETING_SITE_URL || '')
              }
            />
          </Stack>
        </Stack>
      </Paper>
    </Modal>
  );
};
